/**
 * Confirmation Modal Template Component
 * @component
 * @param {Object} props - The component props
 * @param {React.ReactNode} props.actions - Action buttons for the modal.
 * @param {string} [props.mainText=""] - Main text content of the modal.
 * @param {string} [props.secondaryText=""] - Secondary text content of the modal.
 * @param {boolean} props.open - Boolean to control modal visibility.
 * @param {Function} props.onClose - Function to close the modal.
 * @param {string} [props.maxWidth="sm"] - Maximum width of the modal.
 * @param {boolean} [props.fullWidth=true] - Whether the modal should take full width.
 * @param {string} [props.iconType="delete"] - Type of icon to be displayed in the modal.
 * @param {Array<string>} [props.colors=defaultColors] - Background colors for the modal.
 * @returns {React.ReactNode} The rendered ConfirmModalTemplate component.
 * @throws Will throw an error if 'onClose' or 'actions' are not provided.
 */
import {
    Dialog,
    useTheme,
    Box,
    Typography,
    Stack,
    DialogActions,
    IconButton,
  } from "@mui/material";
  import React, { useEffect, useMemo } from "react";
  import LogoutIcon from '@mui/icons-material/Logout';
  import CloseIcon from "@mui/icons-material/Close";
  
  // Default colors for the modal background
  const defaultColors = ["#FEF3F2", "#FEE4E2"];
  
  // Functional component for a confirmation modal
  const ConfirmModalTemplate = ({
    actions,
    mainText = "",
    secondaryText = "",
    open,
    onClose,
    maxWidth = "sm",
    fullWidth = true,
    iconType = "delete",
    colors = defaultColors,//Warming colours=['#FFF7E0','#ffeebd']||SaveColours=['#E8EFFA',"#DAE9FF"]
  }) => {
    // Accessing the theme from Material-UI
    const theme = useTheme();
  
    // Generating the appropriate icon based on the specified iconType
    const Icons = useMemo(() => {
      switch (iconType) {
        case "delete":
          return (
            // Trash icon with red color
            <i className="fi fi-rs-trash" style={{ color: "#d13c31", fontSize: 24 }}></i>
          );
        case "save":
          return (
            // Checkmark icon with the primary theme color
            <i className="fi fi-rs-check" style={{ color: theme.palette.primary.main, fontSize: 24 }}></i>
          );
        case "warning":
          return (
            // Warning triangle icon with the warning theme color
            <i className="fi fi-rs-triangle-warning" style={{ color: theme.palette.warning.main, fontSize: 24 }}></i>
          );
        case "mail":
          return (
            // Mail icon with the primary theme color
            <i className="fi fi-rs-envelope" style={{ color: theme.palette.primary.main, fontSize: 24 }}></i>
          );
        case "subscription":
          return (
            // Warning icon with primary theme color for subscription
            <i className="fi fi-rs-triangle-warning" style={{ color: theme.palette.primary.main, fontSize: 24 }}></i>
          );
          case "warning-subscription":
            return (
              // Warning triangle icon with the warning theme color
              <i className="fi fi-rs-triangle-warning" style={{ color: "#d13c31", fontSize: 24 }}></i>
            );
            case "logout":
              return (<LogoutIcon color="error" sx={{mt:0.5}}></LogoutIcon>)
        default:
          return null;
      }
    }, [iconType, theme.palette.primary.main, theme.palette.warning.main]);
    
  
    // useEffect to check for required props
    useEffect(() => {
   
      if (!actions) {
        throw new Error(
          'Error: Actions buttons not found. Please provide "actions"'
        );
      }
    }, [actions]);
  
    // Rendering the confirmation modal
    return (
      <Dialog
        open={open}
        onClose={onClose?onClose:null}
        maxWidth={maxWidth}
        fullWidth={fullWidth}
      >
        {/* Modal content */}
        <Box sx={{ position: "relative", padding: "24px" }}>
          {/* Header section with icon and close button */}
          <Stack direction={"row"} justifyContent={"space-between"}>
            <div
              className="delete-modal-icon"
              style={{ backgroundColor: colors[0] }}
            >
              <div
                style={{
                  backgroundColor: colors[1],
                }}
              >
                {Icons}
              </div>
            </div>
            {onClose && typeof onClose === 'function' && (
              <IconButton onClick={onClose} size="large" sx={{ mt: -2, mr: -2 }}>
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
          {/* Main and secondary text content */}
          <Stack sx={{ mt: 1.3 }}>
            <Typography
              sx={{ fontWeight: 600, fontSize: "18px", lineHeight: "28px" }}
            >
              {mainText}
            </Typography>
            <Typography
              sx={{ fontSize: "14px", lineHeight: "20px", fontWeight: 400,whiteSpace:'pre-line' }}
            >
              {secondaryText}
            </Typography>
          </Stack>
        </Box>
        {/* Action buttons section */}
        <DialogActions sx={{ justifyContent: "flex-start", px: 3, pb: 3 }}>
          {actions}
        </DialogActions>
      </Dialog>
    );
  };
  
  // Exporting the ConfirmModalTemplate component
  export default ConfirmModalTemplate;
  